import {
  AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild, inject,
} from '@angular/core';
import {MatProgressSpinnerModule} from '@angular/material/progress-spinner';
import {MatTooltipModule} from '@angular/material/tooltip';
import {TranslateModule} from '@ngx-translate/core';
import {TablerIconsModule} from 'angular-tabler-icons';
import {firstValueFrom} from 'rxjs';
import type {PDFDocumentProxy, RenderTask} from 'pdfjs-dist';
import {FilemanagerApiService} from '@services/api/filemanager/filemanager-api.service';

const ZOOM_STEPS = [0.75, 1, 1.25, 1.5, 2];

/**
 * Visualizzatore PDF dell'app (pdf.js): apre il documento della fonte alla pagina citata.
 * Il PDF arriva decifrato dal backend e resta nel browser; pdf.js si carica solo alla prima apertura.
 */
@Component({
  selector: 'app-pdf-viewer',
  standalone: true,
  imports: [MatProgressSpinnerModule, MatTooltipModule, TranslateModule, TablerIconsModule],
  templateUrl: './pdf-viewer.component.html',
  styleUrl: './pdf-viewer.component.scss',
})
export class PdfViewerComponent implements OnChanges, AfterViewInit, OnDestroy {
  @Input({required: true}) fileUuid!: string;
  @Input() title = '';
  @Input() page = 1;
  /** Pagine citate dalla risposta: scorciatoie nella barra */
  @Input() citedPages: number[] = [];
  @Output() closed = new EventEmitter<void>();

  @ViewChild('canvas') canvas?: ElementRef<HTMLCanvasElement>;
  @ViewChild('stage') stage?: ElementRef<HTMLDivElement>;

  private filemanagerApi = inject(FilemanagerApiService);

  loading = true;
  error = false;
  current = 1;
  pageCount = 0;
  zoomIndex = 1;

  private pdf: PDFDocumentProxy | null = null;
  private loadedUuid: string | null = null;
  private bytes: Blob | null = null;
  private renderTask: RenderTask | null = null;
  private resizeObserver?: ResizeObserver;
  private viewReady = false;

  ngAfterViewInit(): void {
    this.viewReady = true;
    // La pagina si ridisegna sulla larghezza del pannello quando questo cambia
    this.resizeObserver = new ResizeObserver(() => this.render());
    if (this.stage) {
      this.resizeObserver.observe(this.stage.nativeElement);
    }
    this.load();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.viewReady) {
      return;
    }
    if (changes['fileUuid'] && this.fileUuid !== this.loadedUuid) {
      this.load();
    } else if (changes['page']) {
      this.goTo(this.page);
    }
  }

  ngOnDestroy(): void {
    this.resizeObserver?.disconnect();
    this.renderTask?.cancel();
    this.pdf?.destroy();
  }

  get zoomLabel(): string {
    return `${Math.round(ZOOM_STEPS[this.zoomIndex] * 100)}%`;
  }

  isCited(page: number): boolean {
    return this.citedPages.includes(page);
  }

  private async load(): Promise<void> {
    const uuid = this.fileUuid;
    this.loading = true;
    this.error = false;
    this.renderTask?.cancel();
    await this.pdf?.destroy();
    this.pdf = null;
    try {
      const [pdfjs, blob] = await Promise.all([
        // Build "legacy": funziona anche con i browser meno recenti (es. Safari su iPhone non aggiornati)
        import('pdfjs-dist/legacy/build/pdf.mjs'),
        firstValueFrom(this.filemanagerApi.getFileBytes(uuid)) as unknown as Promise<Blob>,
      ]);
      if (uuid !== this.fileUuid) {
        return;  // nel frattempo e' stato chiesto un altro documento
      }
      if (!pdfjs.GlobalWorkerOptions.workerPort) {
        // Worker incluso dalla build Angular come file .js: i server lo servono gia' con un MIME
        // JavaScript, mentre un .mjs puo' arrivare come application/octet-stream ed essere rifiutato.
        pdfjs.GlobalWorkerOptions.workerPort = new Worker(
          new URL('./pdf.worker', import.meta.url),
          {type: 'module'},
        );
      }
      this.bytes = blob;
      this.pdf = await pdfjs.getDocument({data: new Uint8Array(await blob.arrayBuffer())}).promise;
      this.loadedUuid = uuid;
      this.pageCount = this.pdf.numPages;
      this.loading = false;
      await this.goTo(this.page);
    } catch {
      this.loading = false;
      this.error = true;
    }
  }

  async goTo(page: number): Promise<void> {
    if (!this.pdf) {
      return;
    }
    this.current = Math.min(Math.max(1, page || 1), this.pageCount);
    this.stage?.nativeElement.scrollTo({top: 0});
    await this.render();
  }

  prev(): void {
    this.goTo(this.current - 1);
  }

  next(): void {
    this.goTo(this.current + 1);
  }

  zoom(step: number): void {
    this.zoomIndex = Math.min(Math.max(0, this.zoomIndex + step), ZOOM_STEPS.length - 1);
    this.render();
  }

  /** Il PDF intero in una nuova scheda, alla pagina corrente (visualizzatore del browser). */
  openInTab(): void {
    if (!this.bytes) {
      return;
    }
    const url = URL.createObjectURL(new Blob([this.bytes], {type: 'application/pdf'}));
    window.open(`${url}#page=${this.current}`, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  }

  private async render(): Promise<void> {
    const canvas = this.canvas?.nativeElement;
    const stage = this.stage?.nativeElement;
    if (!this.pdf || !canvas || !stage) {
      return;
    }
    this.renderTask?.cancel();
    const page = await this.pdf.getPage(this.current);
    const base = page.getViewport({scale: 1});
    // Larghezza del pannello, poi lo zoom scelto; il doppio dei pixel sugli schermi ad alta densita'
    const fit = Math.max(200, stage.clientWidth - 32) / base.width;
    const viewport = page.getViewport({scale: fit * ZOOM_STEPS[this.zoomIndex]});
    const ratio = window.devicePixelRatio || 1;
    canvas.width = Math.floor(viewport.width * ratio);
    canvas.height = Math.floor(viewport.height * ratio);
    canvas.style.width = `${Math.floor(viewport.width)}px`;
    canvas.style.height = `${Math.floor(viewport.height)}px`;
    const context = canvas.getContext('2d');
    if (!context) {
      return;
    }
    this.renderTask = page.render({
      canvasContext: context,
      viewport,
      transform: ratio !== 1 ? [ratio, 0, 0, ratio, 0, 0] : undefined,
    });
    try {
      await this.renderTask.promise;
    } catch {
      // Ridisegno annullato da uno piu' recente (cambio pagina, zoom, ridimensionamento)
    }
  }
}
